import {VivoPortal} from "@utils/AppConstants";
import {VivoRootModel} from "@utils/Models";

import EnumMemberGroupAliases = VivoPortal.EnumMemberGroupAliases;

/** Everything an {@link AccessRule} may base its decision on; always built from the current authenticated context. */
export interface AccessRuleContext {
    /** Role bitmask of the current (possibly impersonated) user. */
    roleMask: EnumMemberGroupAliases;
    /** Current lab id, or -1 while unknown. */
    labId: number;
    /** Current division (segment) id, or -1 while unknown. */
    divisionId: number;
    /** Current root model; undefined while the context is absent or loading. */
    rootModel: VivoRootModel | undefined;
    /** True when a token exists. */
    hasToken: boolean;
}

/**
 * Optional extra access decision for `Authorized` / `ProtectedRoute`. It is ANDed with the existing
 * role / lab / division filters, so it can express alternatives (OR) that those filters cannot.
 * Must be a pure function of the context; a rule must fail closed (return false) when the data it needs is missing.
 */
export type AccessRule = (context: AccessRuleContext) => boolean;

export function buildAccessRuleContext(
    roleMask: EnumMemberGroupAliases,
    rootModel: VivoRootModel | undefined | null,
    token: string | null | undefined
): AccessRuleContext {
    return {
        roleMask,
        labId: rootModel?.CurrentUserProfile?.LabId ?? -1,
        divisionId: rootModel?.CurrentUserProfile?.DivisionId ?? -1,
        rootModel: rootModel ?? undefined,
        hasToken: !!token,
    };
}

/** Evaluates an optional rule: no rule means no extra restriction; a rule requires a token and must return true. */
export function isAccessRuleSatisfied(rule: AccessRule | undefined, context: AccessRuleContext): boolean {
    if (!rule)
        return true;
    return context.hasToken && rule(context) === true;
}
